import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {mapLayersControl} from 'shared';
import {InterventionsRequest} from '../interventions-toolbar/interventions-toolbar.component';
import {mapStreetLayer} from 'shared';
import {GeoJSON, Map, Polygon, tileLayer, FeatureGroup, Control} from 'leaflet';
import {ReplaySubject} from 'rxjs';
import {first} from 'rxjs/operators';
import * as L from 'leaflet';
import 'leaflet-draw';
import * as turf from '@turf/turf';
import {TimePieChartEntry} from '../../reports/charts/time-pie-chart/time-pie-chart.component';
import {ReconstructedIntervention, ReconstructedInterventionService} from '../../stores/reconstructed-intervention.service';
import {ValidateDialogComponent} from '../validate-dialog/validate-dialog.component';
import {RejectDialogComponent} from '../reject-dialog/reject-dialog.component';
import checkIcon from '@iconify/icons-mdi/check-circle';
import closeIcon from '@iconify/icons-mdi/close-circle';
import editIcon from '@iconify/icons-mdi/pencil';
import saveIcon from '@iconify/icons-mdi/content-save';
import cancelIcon from '@iconify/icons-mdi/close';
import {User, notify} from 'shared';

@Component({
  selector: 'app-reconstructed-intervention-report',
  templateUrl: './intervention-report.component.html',
  styleUrls: ['./intervention-report.component.scss']
})
export class ReconstructedInterventionReportComponent implements OnInit {

  @Input()
  visible: boolean;

  @Output()
  visibleChange = new EventEmitter<boolean>();

  @Input()
  intervention: ReconstructedIntervention;

  @Input()
  request: InterventionsRequest;


  private map: Map;
  private _mapSubject = new ReplaySubject<Map>(1);
  mapReady$ = this._mapSubject.asObservable();
  selectedTab = 0;

  layers = [
    {
      name: 'Hybride',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
    {
      name: 'Satellite',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
  ];

  mapOptions = {
    layers: this.layers.map(l => l.layer),
    zoom: 7,
    minZoom: 0,
    maxZoom: 21,
    maxNativeZoom: 21,
    preferCanvas: false,
    center: [33.626756199999996, -7.4964748] as [number, number]
  };

  private workingMultiPolygon: GeoJSON;
  private trajectoryLineString: GeoJSON;
  private fieldPolygon: Polygon<any>;
  timeReport: TimePieChartEntry[];
  title: string;

  @ViewChild('validateDialog') validateDialog: ValidateDialogComponent;
  @ViewChild('rejectDialog') rejectDialog: RejectDialogComponent;
  
  checkIcon = checkIcon;
  closeIcon = closeIcon;
  editIcon = editIcon;
  saveIcon = saveIcon;
  cancelIcon = cancelIcon;

  @Output() onActionSuccess = new EventEmitter<void>();

  canValidate: boolean = false;
  canReject: boolean = false;

  // Editing state
  isEditingPolygon: boolean = false;
  private editableFeatureGroup: FeatureGroup;
  private drawControl: Control.Draw;
  private originalWorkingPolygonGeoJSON: any; // Backup for cancel
  isSavingPolygon: boolean = false;
  private preserveMapView: boolean = false; // Flag to prevent zoom reset after save

  constructor(
    public user: User,
    private interventionService: ReconstructedInterventionService
  ) {
    this.close = this.close.bind(this);
  }

  ngOnInit(): void {
    // Permissions - set in ngOnInit to avoid change detection issues
    this.canValidate = this.user.can('validate reconstructed_interventions');
    this.canReject = this.user.can('reject reconstructed_interventions');
  }

  onMapReady(map: Map) {
    this.map = map;
    this._mapSubject.next(map);
    
    // Initialize editable feature group for polygon editing
    this.editableFeatureGroup = new FeatureGroup();
    this.editableFeatureGroup.addTo(this.map);
  }

  closed(e: any) {
    this.visibleChange.emit(false);
  }

  onShowing(e: any) {

    this.buildTimeReport();

    this.mapReady$.pipe(first()).subscribe(() => {

      // Clean the map
      this.cleanMap();

      // Add field to map
      if (this.intervention.field) {
        this.addFieldToMap();

      }

      // Add working multipolygon (field polygons) - ONLY if it exists
      // This represents actual field work clusters (green polygon)
      // This is separate from trajectory - it shows the clustered work area only
      if (this.intervention.working_multipolygon_geojson) {
        // Don't auto-fit bounds if we just saved (preserve user's view)
        this.addWorkingMultipolygonToMap(!this.preserveMapView);
        this.preserveMapView = false; // Reset flag after use
      }

      // Add trajectory (actual path taken) - show separately from working polygon
      // Trajectory shows the complete path including road travel (blue line)
      // This is ALWAYS separate from the working polygon - they should never overlap or be the same
      if (this.intervention.trajectory_linestring_geojson) {
        this.addTrajectoryToMap();
      }
    });
  }

  onShown(e: any) {
    this.title = `Rapport d'Intervention Reconstruite: ${this.intervention.tractor_name || 'N/A'} - ${this.intervention.trailer_name || 'N/A'}`;
    
    // Debug logging for edit button visibility
    console.log('[ReconstructedInterventionReport] Edit button check:', {
      canValidate: this.canValidate,
      isPending: this.isPendingValidation(),
      status: this.intervention?.status,
      canEdit: this.canEditPolygon(),
      hasPolygon: !!this.intervention?.working_multipolygon_geojson
    });
  }

  addFieldToMap() {
    const f = this.intervention.field;
    if (!f || !f.polygon_area) {
      return;
    }
    // For reconstructed interventions, we don't have field geometry from backend yet
    // This is a placeholder - field polygon would need backend to return field geometry
  }

  addWorkingMultipolygonToMap(fitBounds: boolean = true) {
    // Convert working_multipolygon_geojson to GeoJSON (coordinates are swapped for Leaflet)
    const geoJson = this.interventionToGeoJSON();
    if (!geoJson) {
      return;
    }
    
    // Remove existing polygon if any
    if (this.workingMultiPolygon) {
      this.removeWorkingMultipolygonFromMap();
    }
    
    this.workingMultiPolygon = geoJson;
    
    // Ensure the polygon is styled - setStyle is already applied via onEachFeature in interventionToGeoJSON
    // But also apply it here to make sure it's visible
    this.workingMultiPolygon.setStyle({
      fillColor: '#4ef34a', // Green fill for working area (clustered field work)
      fill: true,
      weight: 3, // Thicker border to make polygon clearly visible
      interactive: true,
      fillOpacity: 0.6, // More opaque to clearly show the work area polygon
      color: '#04b800', // Green border for working area polygons
      stroke: true,
    });
    
    // For MultiPolygon/FeatureCollection, iterate through each layer and style it
    // This ensures each polygon in the multipolygon is clearly visible
    this.workingMultiPolygon.eachLayer((layer: any) => {
      if (layer.setStyle) {
        layer.setStyle({
          fillColor: '#4ef34a', // Green fill for working area polygons
          fill: true,
          weight: 3, // Thicker border
          interactive: true,
          fillOpacity: 0.6, // Visible fill
          color: '#04b800', // Green border
          stroke: true,
        });
      }
    });
    
    this.workingMultiPolygon.addTo(this.map);
    
    // Log for debugging
    console.log('[ReconstructedInterventionReport] Added working polygon to map:', {
      hasGeoJson: !!geoJson,
      layers: this.workingMultiPolygon?.getLayers?.()?.length || 0,
      fitBounds: fitBounds
    });
    
    // Fit map to bounds only if requested (default on initial load, skip after edit save)
    if (fitBounds) {
      try {
        const bounds = this.workingMultiPolygon.getBounds();
        if (bounds && bounds.isValid()) {
          const northEast = bounds.getNorthEast();
          const southWest = bounds.getSouthWest();
          const hasSize = (northEast.lat !== southWest.lat) || (northEast.lng !== southWest.lng);
          
          if (hasSize) {
            // If we have a field polygon, combine bounds, otherwise just fit to working polygon
            if (this.fieldPolygon) {
              const fieldBounds = this.fieldPolygon.getBounds();
              if (fieldBounds && fieldBounds.isValid()) {
                bounds.extend(fieldBounds);
              }
            }
            this.map.fitBounds(bounds, {
              padding: [15, 15],
              maxZoom: 22,
            });
          } else {
            // Zero-size bounds - center on the point instead
            this.map.setView([northEast.lat, northEast.lng], 15);
          }
        }
      } catch (e) {
        // If bounds fitting fails, ignore
      }
    }
  }

  /**
   * Converts intervention working_multipolygon_geojson to Leaflet GeoJSON (like TrailerIntervention.toGeoJSON())
   * TrailerIntervention.toGeoJSON() does: return new GeoJSON<MultiPolygon>(this.working_multipolygon_geojson);
   * 
   * After backend fix, working_multipolygon_geojson should be a JSON object (json.RawMessage),
   * but we handle both object and string for backward compatibility.
   */
  private interventionToGeoJSON(): GeoJSON | null {
    if (!this.intervention || !this.intervention.working_multipolygon_geojson) {
      console.warn('[ReconstructedInterventionReport] No working_multipolygon_geojson for intervention:', this.intervention?.id);
      return null;
    }
    
    let geoJsonData: any = this.intervention.working_multipolygon_geojson;
    
    // If it's a string (legacy format), parse it
    if (typeof geoJsonData === 'string') {
      try {
        geoJsonData = JSON.parse(geoJsonData);
      } catch (e) {
        console.error('[ReconstructedInterventionReport] Failed to parse GeoJSON string:', e);
        return null;
      }
    }
    
    // Validate structure
    if (!geoJsonData || !geoJsonData.type || !geoJsonData.coordinates) {
      console.warn('[ReconstructedInterventionReport] Invalid GeoJSON structure:', geoJsonData);
      return null;
    }
    
    // Backend stores coordinates as [lng, lat] (GeoJSON standard)
    // Leaflet expects [lat, lng], so we need to swap coordinates
    const swappedGeoJson = this.swapCoordinates(geoJsonData);
    
    // Create GeoJSON with swapped coordinates
    // Use onEachFeature to style each polygon in MultiPolygon individually
    try {
      const geoJsonLayer = new GeoJSON(swappedGeoJson, {
        style: (feature: any) => {
          return {
            fillColor: '#4ef34a', // Green fill for working area
            fill: true,
            weight: 3, // Thicker border to make polygon clearly visible
            interactive: true,
            fillOpacity: 0.6, // More opaque to clearly show the work area polygon
            color: '#04b800', // Green border
            stroke: true,
          };
        },
        onEachFeature: (feature: any, layer: any) => {
          // Ensure each polygon is properly styled
          layer.setStyle({
            fillColor: '#4ef34a',
            fill: true,
            weight: 3,
            interactive: true,
            fillOpacity: 0.6,
            color: '#04b800',
            stroke: true,
          });
        }
      });
      return geoJsonLayer;
    } catch (e) {
      console.error('[ReconstructedInterventionReport] Failed to create GeoJSON layer:', e);
      return null;
    }
  }

  /**
   * Swaps coordinates from [lng, lat] to [lat, lng] for Leaflet
   * Backend stores as GeoJSON standard [lng, lat], but Leaflet uses [lat, lng]
   */
  private swapCoordinates(geoJson: any): any {
    if (!geoJson || !geoJson.coordinates) {
      return geoJson;
    }

    // Deep clone the GeoJSON object
    const swapped = JSON.parse(JSON.stringify(geoJson));
    
    // Recursively swap coordinates based on GeoJSON type
    if (swapped.type === 'Point') {
      // Point: [lng, lat] -> [lat, lng]
      if (Array.isArray(swapped.coordinates) && swapped.coordinates.length >= 2) {
        const [lng, lat, ...rest] = swapped.coordinates;
        swapped.coordinates = [lat, lng, ...rest];
      }
    } else if (swapped.type === 'MultiPolygon' || swapped.type === 'Polygon') {
      // MultiPolygon/Polygon: swap each coordinate pair
      swapped.coordinates = this.swapCoordinateArray(swapped.coordinates);
    } else if (swapped.type === 'LineString' || swapped.type === 'MultiLineString') {
      swapped.coordinates = this.swapCoordinateArray(swapped.coordinates);
    }

    return swapped;
  }

  /**
   * Recursively swaps coordinates in nested arrays (for MultiPolygon, Polygon, etc.)
   * Converts [lng, lat] to [lat, lng]
   */
  private swapCoordinateArray(coords: any): any {
    if (!Array.isArray(coords)) {
      return coords;
    }

    // If this is a coordinate pair [lng, lat], swap it to [lat, lng]
    if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const [lng, lat, ...rest] = coords;
      return [lat, lng, ...rest];
    }

    // Otherwise, recursively process nested arrays
    return coords.map((item: any) => this.swapCoordinateArray(item));
  }

  /**
   * Extract sample coordinates from nested coordinate arrays for debugging
   */
  private extractSampleCoordinates(coords: any, depth: number = 0, maxDepth: number = 4, maxSamples: number = 5): any {
    if (depth >= maxDepth || maxSamples <= 0) {
      return '[too deep or max samples reached]';
    }
    
    if (!Array.isArray(coords) || coords.length === 0) {
      return coords;
    }
    
    // If it's an array of numbers, it's a coordinate pair
    if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return coords.slice(0, Math.min(coords.length, 3)); // Return [lng, lat] or [lng, lat, elevation]
    }
    
    // Otherwise, it's nested arrays - take first few samples
    const samples = coords.slice(0, Math.min(coords.length, maxSamples));
    return samples.map((item: any) => this.extractSampleCoordinates(item, depth + 1, maxDepth, maxSamples));
  }

  removeFieldFromMap() {
    if (this.fieldPolygon) {
      this.fieldPolygon.remove();
    }
  }

  removeWorkingMultipolygonFromMap() {
    if (this.workingMultiPolygon) {
      this.workingMultiPolygon.remove();
    }
  }

  addTrajectoryToMap() {
    // Convert trajectory_linestring_geojson to GeoJSON (coordinates are swapped for Leaflet)
    const geoJson = this.trajectoryToGeoJSON();
    if (!geoJson) {
      return;
    }

    // Remove existing trajectory if any
    if (this.trajectoryLineString) {
      this.removeTrajectoryFromMap();
    }

    this.trajectoryLineString = geoJson;
    // Style trajectory (blue line) - COMPLETE PATH including road travel
    // This is ALWAYS separate from the working polygon - shows the actual GPS path taken
    this.trajectoryLineString.setStyle({
      color: '#007bff', // Blue color for trajectory (complete path)
      weight: 4, // Slightly thicker to make it more visible
      opacity: 0.9, // More opaque to distinguish from working area
      interactive: true,
      dashArray: '5, 5', // Dashed line to visually distinguish from working area border
    });
    this.trajectoryLineString.addTo(this.map);

    // Fit bounds to show both trajectory and working area if both exist
    // They should be displayed separately - trajectory (blue) and working area (green)
    try {
      if (this.workingMultiPolygon) {
        // Both exist - fit bounds to include both, but keep them visually separate
        const workingBounds = this.workingMultiPolygon.getBounds();
        const trajectoryBounds = this.trajectoryLineString.getBounds();
        if (workingBounds && workingBounds.isValid() && trajectoryBounds && trajectoryBounds.isValid()) {
          // Extend bounds to include both (trajectory may extend beyond working area)
          workingBounds.extend(trajectoryBounds);
          this.map.fitBounds(workingBounds, {
            padding: [20, 20], // More padding to see both clearly
            maxZoom: 22,
          });
        }
      } else {
        // Only trajectory exists - fit to it
        const bounds = this.trajectoryLineString.getBounds();
        if (bounds && bounds.isValid()) {
          this.map.fitBounds(bounds, {
            padding: [15, 15],
            maxZoom: 22,
          });
        }
      }
    } catch (e) {
      // If bounds fitting fails, ignore
    }
  }

  /**
   * Converts intervention trajectory_linestring_geojson to Leaflet GeoJSON
   */
  private trajectoryToGeoJSON(): GeoJSON | null {
    if (!this.intervention || !this.intervention.trajectory_linestring_geojson) {
      return null; // No trajectory available
    }

    let geoJsonData: any = this.intervention.trajectory_linestring_geojson;

    // If it's a string (legacy format), parse it
    if (typeof geoJsonData === 'string') {
      try {
        geoJsonData = JSON.parse(geoJsonData);
      } catch (e) {
        console.error('[ReconstructedInterventionReport] Failed to parse trajectory GeoJSON string:', e);
        return null;
      }
    }

    // Validate structure
    if (!geoJsonData || !geoJsonData.type || !geoJsonData.coordinates) {
      return null;
    }

    // Backend stores coordinates as [lng, lat] (GeoJSON standard)
    // Leaflet expects [lat, lng], so we need to swap coordinates
    const swappedGeoJson = this.swapCoordinates(geoJsonData);

    // Create GeoJSON with swapped coordinates
    try {
      const geoJsonLayer = new GeoJSON(swappedGeoJson);
      return geoJsonLayer;
    } catch (e) {
      console.error('[ReconstructedInterventionReport] Failed to create trajectory GeoJSON layer:', e);
      return null;
    }
  }

  removeTrajectoryFromMap() {
    if (this.trajectoryLineString) {
      this.trajectoryLineString.remove();
    }
  }

  cleanMap() {
    this.removeFieldFromMap();
    this.removeWorkingMultipolygonFromMap();
    this.removeTrajectoryFromMap();
    // Don't reset map view - let the polygon bounds determine the view
  }

  private buildTimeReport() {
    // Chart shows three separate segments:
    // Travail = total_working_duration (all working time: move + idle + off, 18min)
    // Arrêt = stop_working_duration (idle + off durations during work, shown separately, 5min)
    // Trajet = total_road_duration (all road durations, 5min)
    // Note: Arrêt is a separate segment, so Travail uses total_working_duration (not drive_working_duration)
    this.timeReport = [
      {name: 'Travail', duration: this.intervention.total_working_duration || 0},
      {name: 'Arrêt', duration: this.intervention.stop_working_duration || 0},
      {name: 'Trajet', duration: this.intervention.total_road_duration || 0},
    ];
  }

  selectTab(e: any) {
    this.selectedTab = e.itemIndex;
    
    // When map tab is selected, invalidate size to ensure proper rendering
    if (this.selectedTab === 1 && this.map) {
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 100);
    }
  }

  close() {
    this.visible = false;
  }

  validateIntervention() {
    if (!this.canValidate) {
      notify('Vous n\'avez pas la permission de valider les interventions', 'error', 3000);
      return;
    }
    if (this.validateDialog && this.intervention?.id) {
      this.validateDialog.show(this.intervention.id);
    }
  }

  rejectIntervention() {
    if (!this.canReject) {
      notify('Vous n\'avez pas la permission de rejeter les interventions', 'error', 3000);
      return;
    }
    if (this.rejectDialog && this.intervention?.id) {
      this.rejectDialog.show(this.intervention.id);
    }
  }

  isPendingValidation(): boolean {
    return this.intervention?.status === 'pending_validation';
  }

  getValidationAccuracyLabel(accuracy: string): string {
    if (accuracy === 'CORRECT') return 'Données Correctes';
    if (accuracy === 'APPROXIMATELY_CORRECT') return 'Données Approximativement Correctes';
    return accuracy;
  }

  getValidationAccuracyColor(accuracy: string): string {
    if (accuracy === 'CORRECT') return 'green';
    if (accuracy === 'APPROXIMATELY_CORRECT') return 'yellow';
    return 'gray';
  }

  onValidateSuccess() {
    this.onActionSuccess.emit();
  }

  onRejectSuccess() {
    this.onActionSuccess.emit();
  }

  // Polygon editing methods
  startEditingPolygon() {
    if (!this.workingMultiPolygon || !this.map) {
      notify('Aucun polygone de travail à modifier', 'warning', 3000);
      return;
    }

    this.isEditingPolygon = true;

    // Backup original data for cancel
    this.originalWorkingPolygonGeoJSON = JSON.parse(JSON.stringify(this.intervention.working_multipolygon_geojson));

    // Keep the existing working polygon visible on map (don't remove it)
    // This allows users to see the existing area while drawing new areas
    // The polygon will stay as read-only layer, and new drawings go to editableFeatureGroup

    // Clear editable feature group (for any previous edits)
    this.editableFeatureGroup.clearLayers();

    // Add existing polygons to editable feature group so they're included in the save
    // This ensures union of old + new polygons
    const geoJsonData = this.intervention.working_multipolygon_geojson;
    let parsedData = geoJsonData;
    if (typeof geoJsonData === 'string') {
      parsedData = JSON.parse(geoJsonData);
    }

    // Swap coordinates for Leaflet
    const swappedGeoJson = this.swapCoordinates(parsedData);

    // Validate and clean the GeoJSON before creating editable layers
    const validGeoJson = this.validateAndCleanGeoJSON(swappedGeoJson);
    
    if (validGeoJson) {
      // Add existing polygons to editable feature group (non-interactive copy)
      try {
        const geoJsonLayer = L.geoJSON(validGeoJson, {
          style: {
            fillColor: '#4ef34a',
            fill: true,
            weight: 3,
            interactive: false, // Make non-interactive to avoid edit errors
            fillOpacity: 0.4, // More transparent to show these are "existing"
            color: '#04b800',
            stroke: true,
          }
        });

        geoJsonLayer.eachLayer((layer: any) => {
          try {
            const bounds = layer.getBounds();
            if (bounds && bounds.isValid()) {
              this.editableFeatureGroup.addLayer(layer);
            }
          } catch (e) {
            console.warn('[startEditingPolygon] Skipping layer with invalid bounds:', e);
          }
        });

        console.log('[startEditingPolygon] Added existing polygons to editable group:', this.editableFeatureGroup.getLayers().length);
      } catch (e) {
        console.warn('[startEditingPolygon] Could not add existing polygons to editable group:', e);
      }
    }

    // Initialize draw control for editing
    // Disable vertex editing for existing polygons since they may have coordinate issues
    // Instead, users can delete old polygons and draw new ones
    this.drawControl = new L.Control.Draw({
      edit: {
        featureGroup: this.editableFeatureGroup,
        edit: false, // Disable vertex editing to avoid coordinate errors
        remove: false // Also disable remove for now
      },
      draw: {
        polygon: {}, // Allow drawing new polygons
        polyline: false,
        rectangle: {}, // Allow drawing rectangles
        circle: false,
        marker: false,
        circlemarker: false
      }
    });

    this.drawControl.addTo(this.map);

    // Listen for draw events (new polygons created)
    this.map.on(L.Draw.Event.CREATED, (e: any) => {
      const layer = e.layer;
      console.log('[Draw.CREATED] New polygon created:', layer.toGeoJSON());
      this.editableFeatureGroup.addLayer(layer);
    });

    // Add button to clear all existing polygons and start fresh
    const clearButton = L.DomUtil.create('button', 'leaflet-bar leaflet-control');
    clearButton.innerHTML = 'Effacer Tout';
    clearButton.style.cssText = 'padding: 5px 10px; background: white; border: 2px solid #ccc; cursor: pointer; font-size: 12px;';
    clearButton.onclick = () => {
      if (confirm('Voulez-vous effacer tous les polygones existants et recommencer?')) {
        this.editableFeatureGroup.clearLayers();
        notify('Tous les polygones ont été effacés. Vous pouvez maintenant dessiner de nouveaux polygones.', 'info', 3000);
      }
    };
    
    // Position the clear button below the draw controls
    const controlContainer = this.map.getContainer().querySelector('.leaflet-top.leaflet-left');
    if (controlContainer) {
      const buttonWrapper = L.DomUtil.create('div', 'leaflet-control', controlContainer as HTMLElement);
      buttonWrapper.appendChild(clearButton);
    }

    notify('Mode édition activé. Dessinez de nouveaux polygones pour les ajouter à la zone existante. Utilisez "Effacer Tout" pour recommencer.', 'info', 6000);
  }

  cancelEditingPolygon() {
    if (!this.map) return;

    // Clean up edit mode
    this.cleanupEditMode();

    // Restore original polygon
    this.intervention.working_multipolygon_geojson = this.originalWorkingPolygonGeoJSON;
    this.originalWorkingPolygonGeoJSON = null;

    // Re-add original working polygon to map
    this.addWorkingMultipolygonToMap();

    notify('Modifications annulées', 'info', 2000);
  }

  private cleanupEditMode() {
    this.isEditingPolygon = false;

    // Remove draw control
    if (this.drawControl) {
      this.drawControl.remove();
      this.drawControl = null;
    }

    // Remove event listeners
    if (this.map) {
      this.map.off(L.Draw.Event.CREATED);
    }

    // Remove clear button if it exists
    if (this.map) {
      const controlContainer = this.map.getContainer().querySelector('.leaflet-top.leaflet-left');
      if (controlContainer) {
        const clearButtons = controlContainer.querySelectorAll('button');
        clearButtons.forEach((btn: HTMLButtonElement) => {
          if (btn.innerHTML === 'Effacer Tout') {
            btn.parentElement?.remove();
          }
        });
      }
    }

    // Clear editable feature group
    this.editableFeatureGroup.clearLayers();
  }

  async saveEditedPolygon() {
    if (!this.editableFeatureGroup || !this.map) return;

    this.isSavingPolygon = true;

    try {
      // Extract all polygons from editable feature group
      const layers = this.editableFeatureGroup.getLayers();
      
      if (layers.length === 0) {
        notify('Aucun polygone à sauvegarder', 'warning', 3000);
        this.isSavingPolygon = false;
        return;
      }

      // Convert layers to GeoJSON (Leaflet format with [lat, lng])
      const geoJsonFeatureCollection: any = {
        type: 'FeatureCollection',
        features: []
      };

      layers.forEach((layer: any) => {
        if (layer.toGeoJSON) {
          const feature = layer.toGeoJSON();
          console.log('[saveEditedPolygon] Layer feature:', feature);
          geoJsonFeatureCollection.features.push(feature);
        }
      });

      console.log('[saveEditedPolygon] FeatureCollection:', geoJsonFeatureCollection);

      // Union all polygons to merge overlapping areas
      const unionedFeatureCollection = this.unionPolygons(geoJsonFeatureCollection);
      console.log('[saveEditedPolygon] After union:', unionedFeatureCollection);

      // Convert to MultiPolygon format and swap coordinates back to [lng, lat] for backend
      const multiPolygon = this.convertToMultiPolygon(unionedFeatureCollection);
      console.log('[saveEditedPolygon] MultiPolygon:', multiPolygon);
      
      const backendGeoJson = this.swapCoordinatesBackToBackendFormat(multiPolygon);
      console.log('[saveEditedPolygon] Backend GeoJSON:', JSON.stringify(backendGeoJson).substring(0, 200));

      // Calculate new area
      const areaInSquareMeters = this.calculatePolygonArea(backendGeoJson);
      console.log('[saveEditedPolygon] Calculated area:', areaInSquareMeters);

      // Send update to backend
      const updateData = {
        working_multipolygon_geojson: backendGeoJson,
        working_area: areaInSquareMeters
      };

      console.log('[saveEditedPolygon] Sending to backend:', {
        id: this.intervention.id,
        area: areaInSquareMeters,
        geoJsonType: backendGeoJson.type
      });

      await this.interventionService.patch(this.intervention.id, updateData).toPromise();

      // Update local intervention data
      this.intervention.working_multipolygon_geojson = backendGeoJson;
      this.intervention.working_area = areaInSquareMeters;
      this.intervention.working_area_ha = areaInSquareMeters / 10000;

      // Save current map view (zoom and center) BEFORE any map operations
      const currentZoom = this.map.getZoom();
      const currentCenter = this.map.getCenter();
      
      console.log('[saveEditedPolygon] Preserving view - zoom:', currentZoom, 'center:', currentCenter);

      // Exit edit mode and clean up UI
      this.cleanupEditMode();

      // Remove ONLY the working polygon before re-adding (trajectory doesn't change)
      this.removeWorkingMultipolygonFromMap();

      // Re-render the updated polygon WITHOUT changing map view
      // This will add the new polygon without calling fitBounds
      this.addWorkingMultipolygonToMap(false);

      // Immediately restore the exact map view (no animation, no delay)
      // This must happen synchronously to prevent any automatic bounds adjustment
      this.map.setView(currentCenter, currentZoom, { animate: false });
      
      console.log('[saveEditedPolygon] View preserved - zoom:', currentZoom, 'center:', currentCenter);

      notify('Polygone sauvegardé avec succès', 'success', 3000);
      
      // Set flag to prevent fitBounds when onShowing is triggered by grid reload
      this.preserveMapView = true;
      
      // Delay grid reload to prevent map reset during view restoration
      // The parent grid's onActionSuccess() calls ds.reload() which can trigger change detection
      // and interfere with our map view restoration
      setTimeout(() => {
        this.onActionSuccess.emit();
      }, 100);

    } catch (error) {
      console.error('Error saving polygon:', error);
      notify('Erreur lors de la sauvegarde du polygone', 'error', 3000);
    } finally {
      this.isSavingPolygon = false;
    }
  }

  private unionPolygons(featureCollection: any): any {
    try {
      if (!featureCollection || !featureCollection.features || featureCollection.features.length === 0) {
        console.warn('[unionPolygons] Empty feature collection');
        return featureCollection;
      }

      // Filter only Polygon and MultiPolygon features
      const polygonFeatures = featureCollection.features.filter((f: any) => 
        f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')
      );

      if (polygonFeatures.length === 0) {
        console.warn('[unionPolygons] No polygon features found');
        return featureCollection;
      }

      if (polygonFeatures.length === 1) {
        // Only one polygon, no need to union
        return {
          type: 'FeatureCollection',
          features: polygonFeatures
        };
      }

      // Start with the first polygon
      let unionedPolygon: any = turf.polygon(polygonFeatures[0].geometry.coordinates);

      // Union with each subsequent polygon
      for (let i = 1; i < polygonFeatures.length; i++) {
        try {
          const currentFeature = polygonFeatures[i];
          let currentPolygon;

          if (currentFeature.geometry.type === 'Polygon') {
            currentPolygon = turf.polygon(currentFeature.geometry.coordinates);
          } else if (currentFeature.geometry.type === 'MultiPolygon') {
            currentPolygon = turf.multiPolygon(currentFeature.geometry.coordinates);
          }

          if (currentPolygon) {
            unionedPolygon = turf.union(unionedPolygon, currentPolygon) as any;
          }
        } catch (err) {
          console.error('[unionPolygons] Error unioning polygon at index', i, err);
        }
      }

      // Return as FeatureCollection with single unioned feature
      return {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: unionedPolygon.geometry
        }]
      };
    } catch (err) {
      console.error('[unionPolygons] Error during union operation:', err);
      // Return original feature collection if union fails
      return featureCollection;
    }
  }

  private convertToMultiPolygon(featureCollection: any): any {
    const polygons: any[] = [];

    featureCollection.features.forEach((feature: any) => {
      if (!feature.geometry) {
        console.warn('[convertToMultiPolygon] Feature has no geometry:', feature);
        return;
      }

      if (feature.geometry.type === 'Polygon') {
        // Each Polygon becomes one element in the MultiPolygon array
        polygons.push(feature.geometry.coordinates);
      } else if (feature.geometry.type === 'MultiPolygon') {
        // Flatten MultiPolygon coordinates into individual polygons
        feature.geometry.coordinates.forEach((poly: any) => {
          polygons.push(poly);
        });
      } else {
        console.warn('[convertToMultiPolygon] Unsupported geometry type:', feature.geometry.type);
      }
    });

    if (polygons.length === 0) {
      console.error('[convertToMultiPolygon] No valid polygons found in feature collection');
    }

    return {
      type: 'MultiPolygon',
      coordinates: polygons
    };
  }

  private swapCoordinatesBackToBackendFormat(geoJson: any): any {
    // Leaflet uses [lat, lng], backend expects [lng, lat]
    // This is the reverse of swapCoordinates
    if (!geoJson || !geoJson.coordinates) {
      return geoJson;
    }

    const swapped = JSON.parse(JSON.stringify(geoJson));
    swapped.coordinates = this.swapCoordinateArrayBackward(swapped.coordinates);
    return swapped;
  }

  private swapCoordinateArrayBackward(coords: any): any {
    if (!Array.isArray(coords)) {
      return coords;
    }

    // If this is a coordinate pair [lat, lng], swap it back to [lng, lat]
    if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const [lat, lng, ...rest] = coords;
      return [lng, lat, ...rest];
    }

    // Otherwise, recursively process nested arrays
    return coords.map((item: any) => this.swapCoordinateArrayBackward(item));
  }

  private calculatePolygonArea(geoJson: any): number {
    // Simple area calculation using Turf.js approach
    // For now, return a placeholder - ideally use a library like Turf.js
    // The backend will recalculate the exact area anyway
    try {
      // Create a Leaflet polygon from the GeoJSON to calculate area
      const swapped = this.swapCoordinates(geoJson);
      const tempLayer = L.geoJSON(swapped);
      const bounds = tempLayer.getBounds();
      
      // Rough approximation based on bounds
      const latDiff = bounds.getNorth() - bounds.getSouth();
      const lngDiff = bounds.getEast() - bounds.getWest();
      
      // Convert to meters (rough approximation)
      const latMeters = latDiff * 111320;
      const lngMeters = lngDiff * 111320 * Math.cos((bounds.getNorth() + bounds.getSouth()) / 2 * Math.PI / 180);
      
      return latMeters * lngMeters; // Square meters
    } catch (e) {
      console.error('Error calculating area:', e);
      return this.intervention.working_area || 0;
    }
  }

  canEditPolygon(): boolean {
    // Allow editing if user can validate (since editing is part of the validation workflow)
    // and intervention is pending validation
    return this.canValidate && this.isPendingValidation();
  }

  /**
   * Validates and cleans GeoJSON to ensure all coordinates are valid
   * Removes null/undefined coordinates and invalid polygons
   */
  private validateAndCleanGeoJSON(geoJson: any): any {
    if (!geoJson || !geoJson.type) {
      return null;
    }

    // For MultiPolygon
    if (geoJson.type === 'MultiPolygon') {
      if (!geoJson.coordinates || !Array.isArray(geoJson.coordinates)) {
        return null;
      }

      const cleanedPolygons: any[] = [];

      geoJson.coordinates.forEach((polygon: any) => {
        if (!Array.isArray(polygon)) return;

        const cleanedRings: any[] = [];

        polygon.forEach((ring: any) => {
          if (!Array.isArray(ring)) return;

          const cleanedCoords: any[] = [];

          ring.forEach((coord: any) => {
            // Validate coordinate is an array with 2+ valid numbers
            if (Array.isArray(coord) && 
                coord.length >= 2 && 
                typeof coord[0] === 'number' && 
                typeof coord[1] === 'number' &&
                !isNaN(coord[0]) && !isNaN(coord[1]) &&
                isFinite(coord[0]) && isFinite(coord[1]) &&
                coord[0] !== null && coord[1] !== null) {
              cleanedCoords.push(coord);
            }
          });

          // Need at least 4 coordinates for a valid ring (closed polygon)
          if (cleanedCoords.length >= 4) {
            // Ensure ring is closed (first == last)
            const first = cleanedCoords[0];
            const last = cleanedCoords[cleanedCoords.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
              cleanedCoords.push([first[0], first[1]]);
            }
            cleanedRings.push(cleanedCoords);
          }
        });

        if (cleanedRings.length > 0) {
          cleanedPolygons.push(cleanedRings);
        }
      });

      if (cleanedPolygons.length === 0) {
        return null;
      }

      return {
        type: 'MultiPolygon',
        coordinates: cleanedPolygons
      };
    }

    // For Polygon
    if (geoJson.type === 'Polygon') {
      if (!geoJson.coordinates || !Array.isArray(geoJson.coordinates)) {
        return null;
      }

      const cleanedRings: any[] = [];

      geoJson.coordinates.forEach((ring: any) => {
        if (!Array.isArray(ring)) return;

        const cleanedCoords: any[] = [];

        ring.forEach((coord: any) => {
          if (Array.isArray(coord) && 
              coord.length >= 2 && 
              typeof coord[0] === 'number' && 
              typeof coord[1] === 'number' &&
              !isNaN(coord[0]) && !isNaN(coord[1]) &&
              isFinite(coord[0]) && isFinite(coord[1]) &&
              coord[0] !== null && coord[1] !== null) {
            cleanedCoords.push(coord);
          }
        });

        if (cleanedCoords.length >= 4) {
          const first = cleanedCoords[0];
          const last = cleanedCoords[cleanedCoords.length - 1];
          if (first[0] !== last[0] || first[1] !== last[1]) {
            cleanedCoords.push([first[0], first[1]]);
          }
          cleanedRings.push(cleanedCoords);
        }
      });

      if (cleanedRings.length === 0) {
        return null;
      }

      return {
        type: 'Polygon',
        coordinates: cleanedRings
      };
    }

    return geoJson;
  }
}

